import React, { useEffect, useState } from 'react';
import { Tab, Tabs } from 'baseui/tabs';
import { PageTitle } from '../../Shared';
import { Overrides } from '../../Users/baseuiOverrides';
import { DashboardFrame } from './DashboardFrame';
import { BeaconDashboardTabs } from '../types';

type DashboardTabsViewProps = {
  dashboards: BeaconDashboardTabs[];
  activeTabKey: BeaconDashboardTabs['key'];
  onTabChange: (tabKey: string) => void;
};

// Tab's `renderAll` keeps each tab panel in the DOM (inactive ones hidden via display: none).
// We additionally track which tabs have ever been activated and only render the
// DashboardFrame for those — this avoids fetching all Hex pre-signed URLs
// up-front while ensuring that re-visiting a previously-viewed dashboard reuses
// its existing iframe instead of triggering a fresh single-use URL and cold render.
export const DashboardTabsView = ({
  dashboards,
  activeTabKey,
  onTabChange,
}: DashboardTabsViewProps) => {
  const [visitedKeys, setVisitedKeys] = useState<Set<BeaconDashboardTabs['key']>>(
    () => new Set([activeTabKey])
  );

  useEffect(() => {
    setVisitedKeys(prev => {
      if (prev.has(activeTabKey)) return prev;
      const next = new Set(prev);
      next.add(activeTabKey);
      return next;
    });
  }, [activeTabKey]);

  return (
    <div className="bg-white">
      <PageTitle className="pa4 ml2">Beacon</PageTitle>
      <Tabs
        activeKey={activeTabKey}
        onChange={({ activeKey }) => onTabChange(String(activeKey))}
        overrides={Overrides.Tabs}
        renderAll
      >
        {dashboards.map(dashboard => (
          <Tab title={dashboard.displayName} key={dashboard.key} overrides={Overrides.Tab}>
            {activeTabKey === dashboard.key || visitedKeys.has(dashboard.key) ? (
              <DashboardFrame dashboardKey={dashboard.key} />
            ) : null}
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};
